import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { createPartner, updatePartner } from '../../../../api/apiFunction/partnerServices';

// Modal component for adding or updating partners
const AddOrUpdateModal = ({ isOpen, onClose, partner, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'university',
    referralCode: '',
    rewardType: 'flat', // Always fixed amount per requirements
    rewardValue: '',
    userBonus: {
      type: 'none', // none = no reward, flat = fixed amount, percentage = % discount
      value: ''
    },
    totalSignuplimit: '',
    hasExpiryDate: false,
    expirationDateTime: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = !!partner;

  useEffect(() => {
    if (partner) {
      // Convert UTC datetime to local datetime-local format
      let localDateTime = '';
      if (partner.expirationDate) {
        const date = new Date(partner.expirationDate);
        // Format: YYYY-MM-DDTHH:mm (datetime-local format)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        localDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      setFormData({
        name: partner.name || '',
        email: partner.email || '',
        type: partner.type || 'university',
        referralCode: partner.referralCode || '',
        rewardType: 'flat', // Always fixed amount per requirements
        rewardValue: partner.rewardValue || '',
        userBonus: {
          type: partner.userBonus?.type || 'none',
          value: partner.userBonus?.value || ''
        },
        totalSignuplimit: partner.totalSignuplimit || '',
        hasExpiryDate: !!partner.expirationDate,
        expirationDateTime: localDateTime
      });
    } else {
      setFormData({
        name: '',
        email: '',
        type: 'university',
        referralCode: '',
        rewardType: 'flat', // Always fixed amount per requirements
        rewardValue: '',
        userBonus: {
          type: 'none',
          value: ''
        },
        totalSignuplimit: '',
        hasExpiryDate: false,
        expirationDateTime: ''
      });
    }
    setError('');
  }, [partner, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('userBonus.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        userBonus: {
          ...prev.userBonus,
          [field]: value
        }
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation for required fields
      if (!formData.name.trim()) throw new Error('Name is required');
      if (!isEditMode && (!formData.email || !formData.email.trim())) {
        throw new Error('Email is required');
      }
      if (!isEditMode && formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        throw new Error('Please enter a valid email address');
      }
      if (!formData.rewardValue || formData.rewardValue <= 0) throw new Error('Partner share must be greater than 0');
      if (formData.totalSignuplimit && formData.totalSignuplimit < 0) throw new Error('Signup limit cannot be negative');
      // Referral code is optional for new partners (will be auto-generated if not provided)
      // For updates, keep existing referral code if not provided
      if (!formData.rewardValue || formData.rewardValue <= 0) {
        throw new Error('Partner reward must be greater than 0');
      }

      // Validate user bonus based on type
      if (formData.userBonus.type !== 'none') {
        if (!formData.userBonus.value || formData.userBonus.value <= 0) {
          throw new Error(`User ${formData.userBonus.type === 'flat' ? 'reward amount' : 'discount percentage'} must be greater than 0`);
        }
        if (formData.userBonus.type === 'percentage' && formData.userBonus.value > 100) {
          throw new Error('User discount cannot exceed 100%');
        }
      }

      // Validate expiry date if enabled
      if (formData.hasExpiryDate && !formData.expirationDateTime) {
        throw new Error('Expiry date and time are required when expiry date toggle is enabled');
      }
      if (formData.hasExpiryDate && formData.expirationDateTime) {
        const expiryDateTime = new Date(formData.expirationDateTime);
        if (expiryDateTime <= new Date()) {
          throw new Error('Expiry date and time must be in the future');
        }
      }

      const submitData = {
        name: formData.name,
        email: formData.email,
        type: formData.type,
        referralCode: formData.referralCode || undefined,
        rewardType: formData.rewardType,
        url: window.location.origin, // Backend will use FRONTEND_URL from env if set
        rewardValue: parseFloat(formData.rewardValue) || 0,
        userBonus: {
          type: formData.userBonus.type,
          value: parseFloat(formData.userBonus.value) || 0
        },
        totalSignuplimit: parseInt(formData.totalSignuplimit) || 0
      };

      // Add expirationDate only if it's set
      if (formData.hasExpiryDate && formData.expirationDateTime) {
        submitData.expirationDate = new Date(formData.expirationDateTime).toISOString();
      }


      let response;
      if (isEditMode) {
        response = await updatePartner(partner._id, submitData);
      } else {
        response = await createPartner(submitData);
      }

      if (response?.status === 200 || response?.status === 201) {
        toast.success(`Partner ${isEditMode ? 'updated' : 'created'} successfully!`);
        onSuccess();
        onClose();
      } else {
        throw new Error(response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} partner`);
      }
    } catch (err) {
      const errorMessage = err.message || `Failed to ${isEditMode ? 'update' : 'create'} partner`;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-bd-50">
          <h2 className="text-lg font-semibold text-fg-50">
            {isEditMode ? 'Edit Partner' : 'Add New Partner'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-bg-40 rounded transition-colors"
          >
            <X size={20} className="text-fg-60" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6">
          {/* Grid: 2 columns × 3 rows */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                placeholder="Partner name"
                required
                disabled={loading}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                placeholder="partner@example.com"
                required
                disabled={loading || isEditMode}
              />
              <p className="text-xs text-fg-70 mt-1">{isEditMode ? 'Email cannot be changed' : 'Partner email address'}</p>
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                required
              >
                <option value="university">University</option>
                <option value="company">Company</option>
                <option value="individual">Individual</option>
                <option value="organization">Organization</option>
                <option value="government">Government</option>
                <option value="ngo">NGO</option>
                <option value="startup">Startup</option>
              </select>
            </div>

            {/* Reward Value (Partner Fixed Amount) */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Partner Reward (Fixed Amount USD) *
              </label>
              <input
                type="number"
                name="rewardValue"
                value={formData.rewardValue}
                onChange={handleChange}
                className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 
               focus:outline-none focus:ring-1 focus:ring-ac-02"
                placeholder="5.00"
                min="0"
                step="0.01"
                required
              />
              <p className="text-xs text-fg-70 mt-1">Fixed amount earned per monthly subscription</p>
            </div>

            {/* User Bonus Type */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">User Reward Type</label>
              <select
                name="userBonus.type"
                value={formData.userBonus.type}
                onChange={handleChange}
                className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
              >
                <option value="none">No Reward</option>
                <option value="flat">Fixed Amount (USD)</option>
              </select>
            </div>

            {/* User Bonus Value */}
            {formData.userBonus.type !== 'none' && (
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">
                  {formData.userBonus.type === 'flat' ? 'Fixed Amount (USD)' : 'Discount (%)'}
                </label>
                <input
                  type="number"
                  name="userBonus.value"
                  value={formData.userBonus.value}
                  onChange={handleChange}
                  className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 
                 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  placeholder={formData.userBonus.type === 'flat' ? "5.00" : "30"}
                  min="0"
                  step={formData.userBonus.type === 'flat' ? "0.01" : "1"}
                  max={formData.userBonus.type === 'percentage' ? "100" : undefined}
                />
                <p className="text-xs text-fg-70 mt-1">
                  {formData.userBonus.type === 'flat' 
                    ? 'Fixed monetary discount applied on first billing period' 
                    : 'Percentage discount applied on first billing period'}
                </p>
              </div>
            )}
          </div>

          {/* Expiry Date Row - Full Width */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Expiry Date Toggle */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">Expiry Date</label>
              <div className="flex items-center h-[38px]">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="hasExpiryDate"
                    checked={formData.hasExpiryDate}
                    onChange={handleChange}
                    className="sr-only peer"
                    disabled={loading}
                  />
                  <div className="w-11 h-6 bg-bg-40 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ac-02 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ac-02"></div>
                  <span className="ml-3 text-sm text-fg-50">
                    {formData.hasExpiryDate ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>

            {/* Expiry Date & Time Input (Combined) */}
            {formData.hasExpiryDate && (
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Select Date & Time *</label>
                <input
                  type="datetime-local"
                  name="expirationDateTime"
                  value={formData.expirationDateTime}
                  onChange={handleChange}
                  className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  min={new Date().toISOString().slice(0, 16)}
                  required={formData.hasExpiryDate}
                  disabled={loading}
                />
                <p className="text-xs text-fg-70 mt-1">Partnership will expire on this date and time</p>
              </div>
            )}
          </div>

          {/* Other Fields Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Total Signup Limit */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">Total Signup Limit</label>
              <input
                type="number"
                name="totalSignuplimit"
                value={formData.totalSignuplimit}
                onChange={handleChange}
                className="w-full py-2 px-3 text-sm rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                placeholder="1000 (0 for unlimited)"
                min="0"
              />
              <p className="text-xs text-fg-70 mt-1">Enter 0 for unlimited signups, or a fixed number (e.g., 1000)</p>
            </div>


            {/* Referral Code */}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Referral Code {!isEditMode && <span className="text-xs text-fg-60">(Optional - leave empty for auto-generation)</span>}
              </label>
              <input
                type="text"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleChange}
                disabled={isEditMode && formData.referralCode}
                className="w-full py-2 px-3 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-ac-02 bg-bg-50 text-fg-50"
                placeholder={!isEditMode ? "Leave empty for auto-generation" : formData.referralCode || "PARTNER123"}
              />
            </div>
          </div>



          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-bd-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded-md bg-ac-02 text-white dark:text-black  font-semibold hover:bg-ac-01 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrUpdateModal;
